import type { z } from 'zod'
import type { PermissionKey } from '../rbac'
import { CreateProjectSchema } from '../schemas/project'
import { CreateCustomerSchema } from '../schemas/customer'

/**
 * AI form doldurma aracı (`fill_form`) — ajan bir oluşturma formunu doldurulmuş değerlerle kullanıcının karşısına açar.
 * Kayıt AI tarafından yapılmaz: kullanıcı değerleri forma bakıp düzenler ve kendi yetkisiyle "Kaydet" der.
 * Yeni form: burada kayıt + ilgili sayfada `AiFillState` tüketimi (Projeler/Müşteriler örneği).
 */

export interface AiFormDef {
  key: string
  /** Formun bulunduğu sayfa (RESOURCE_MAP rotası) */
  route: string
  title: string
  permission: PermissionKey
  /** Formda doldurulabilen alanlar → alan doğrulayıcısı (paylaşılan Zod şemasından) */
  fields: Readonly<Record<string, z.ZodTypeAny>>
  /** Alanlar arası kural (ör. bitiş > başlangıç); sorun metinleri döner */
  check?: (values: AiFormValues) => string[]
}

const projectShape = CreateProjectSchema.innerType().shape
const customerShape = CreateCustomerSchema.shape

export const AI_FORMS: readonly AiFormDef[] = [
  {
    key: 'project.create', route: '/projects', title: 'Yeni proje', permission: 'PROJECT_WRITE',
    fields: {
      code: projectShape.code, name: projectShape.name, officeId: projectShape.officeId, pmId: projectShape.pmId,
      customerId: projectShape.customerId, startDate: projectShape.startDate, endDate: projectShape.endDate,
      budget: projectShape.budget, currency: projectShape.currency, description: projectShape.description,
    },
    check: (v) => (typeof v.startDate === 'string' && typeof v.endDate === 'string' && new Date(v.endDate) <= new Date(v.startDate)
      ? ['endDate: Bitiş tarihi başlangıç tarihinden sonra olmalı'] : []),
  },
  {
    key: 'customer.create', route: '/customers', title: 'Yeni müşteri', permission: 'CUSTOMER_WRITE',
    fields: {
      name: customerShape.name, contactPerson: customerShape.contactPerson, contactEmail: customerShape.contactEmail,
      contactPhone: customerShape.contactPhone, sector: customerShape.sector, address: customerShape.address,
      notes: customerShape.notes, tags: customerShape.tags,
    },
  },
]

export type AiFormValues = Record<string, unknown>

/** Sayfaya `navigate(route, { state: { aiFill } })` ile geçen yük */
export interface AiFillState { form: string; values: AiFormValues }

export interface AiFormFillTarget {
  path: string
  label: string
  fill: AiFillState
  /** Henüz doldurulmamış zorunlu alanlar (kaydetmeden önce kullanıcıdan istenmeli) */
  missing: string[]
  /** Alanlar arası kural ihlalleri */
  issues: string[]
}

export class AiFormError extends Error {}

export function resolveAiFormFill(
  input: { form: string; values: AiFormValues },
  perms: ReadonlySet<PermissionKey>,
): AiFormFillTarget {
  const def = AI_FORMS.find((f) => f.key === input.form)
  if (!def || !perms.has(def.permission)) throw new AiFormError(`Form yok ya da yetkiniz yok: ${input.form}`)
  const names = Object.keys(def.fields)
  const unknown = Object.keys(input.values).filter((k) => !names.includes(k))
  if (unknown.length) throw new AiFormError(`${def.key} formunda olmayan alanlar: ${unknown.join(', ')}. Alanlar: ${names.join(', ')}`)
  const problems: string[] = []
  for (const [k, v] of Object.entries(input.values)) {
    const r = def.fields[k]!.safeParse(v)
    if (!r.success) problems.push(`${k}: ${r.error.issues.map((i) => i.message).join('; ')}`)
  }
  if (problems.length) throw new AiFormError(`Geçersiz değerler — ${problems.join(' | ')}`)
  const missing = names.filter((k) => !def.fields[k]!.isOptional() && (input.values[k] === undefined || input.values[k] === null || input.values[k] === ''))
  return { path: def.route, label: def.title, fill: { form: def.key, values: input.values }, missing, issues: def.check?.(input.values) ?? [] }
}

/** `submit_form` için: formun kayıtlı olduğunu ve kullanıcının yetkisini doğrular */
export function resolveAiFormKey(form: string, perms: ReadonlySet<PermissionKey>): AiFormDef {
  const def = AI_FORMS.find((f) => f.key === form)
  if (!def || !perms.has(def.permission)) throw new AiFormError(`Form yok ya da yetkiniz yok: ${form}`)
  return def
}
